import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { useForm } from "react-hook-form";
import Creatable from "react-select/creatable";
import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg";
import "../Transaction.scss";
import TransTable from "../TransTable";
import {
  fetchStockItems,
  fetchStores,
  fetchUnits,
  fetchCustomers,
  fetchTransactionTypes,
  fetchNextTransactionNo,
} from "services/fetchData";
import { isoDate, returnAccumfs, returnTransaction } from "../utils";
import { updateTransactionSale } from "services/createServices";
import { formatMoney } from "utils/helperFunctions";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import PersonIcon from "@material-ui/icons/Person";
import MoneyIcon from "@material-ui/icons/Money";
import PrintIcon from "@material-ui/icons/Print";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";

export default function SalesTransaction() {
  let { type } = useParams();
  const { data: transactionTypes } = useQuery(
    "transactionTypes",
    fetchTransactionTypes
  );

  const { data: nextTransactionNo } = useQuery(
    "nextTransactionNo",
    fetchNextTransactionNo
  );

  //selecting customers
  const customerOptions = [];
  const { data: customers } = useQuery("customers", fetchCustomers);
  customers?.forEach((item) =>
    customerOptions?.push({
      label: item?.name,
      value: item?.name,
    })
  );

  //selecting stores
  const storeOptions = [];
  const { data: stores } = useQuery("stores", fetchStores);
  stores?.forEach((item) =>
    storeOptions?.push({
      label: item?.name,
      value: item?.name,
    })
  );

  //selecting units
  const { data: unitData } = useQuery("storeUnits", fetchUnits);

  // item options for selecting items
  const itemOptions = [];
  const { data: stockItems } = useQuery("stockItems", fetchStockItems);
  stockItems?.forEach((item) =>
    itemOptions?.push({
      label: item?.name,
      value: item?.name,
      price: item?.price_1,
      code: item?.code,
      weight: item?.weight,
      itemid: item?.id,
    })
  );

  const [customerSelected, setCustomerSelected] = useState();
  const [storeSelected, setStoreSelected] = useState();
  const [itemSelected, setItemSelected] = useState();
  const [itemPrice, setItemPrice] = useState(0);
  const [selectedItemValue, setSelectedItemValue] = useState("");

  const handleCustomerSelect = (selectedOption) => {
    setCustomerSelected(selectedOption);
  };

  const handleStoreSelect = (selectedOption) => {
    setStoreSelected(selectedOption);
  };

  const handleItemSelect = (selectedOption) => {
    setItemSelected(selectedOption);
    setItemPrice(selectedOption.price);
    setSelectedItemValue(selectedOption.value);
  };

  // persist transaction insert
  const localTransactions = localStorage.getItem("transaction");
  const initialTransactions = localTransactions
    ? JSON.parse(localTransactions)
    : [];

  // Handle Inputs
  const [tableData, setTableData] = useState(initialTransactions);
  const [tableDataChange, setTableDataChange] = useState(false);
  const [sellingPrice, setSellingPrice] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  // Payments methods
  const [cash, setCash] = useState(0);
  const [mpesa, setMpesa] = useState(0);
  const [card, setCard] = useState(0);
  const [ezypay, setEzypay] = useState(0);
  const [loyalty, setLoyalty] = useState(0);
  const [tenderedAmt, setTenderedAmt] = useState(0);
  const [amountChange, setAmountChange] = useState(false);
  const [change, setChange] = useState(0);
  const { register, handleSubmit, reset } = useForm();
  const handleDataInsert = (data, e) => {
    data.amount = 0;
    data.bal_cf = 0;
    data.code = itemSelected?.code;
    data.itemid = itemSelected?.itemid;
    data.weight = itemSelected?.weight;
    data.cost_price = 0;
    data.date = isoDate;
    data.disc = 0;
    data.disc_price = 0;
    data.id = 0;
    data.levy_1 = 0;
    data.levy_2 = 0;
    data.levy_3 = 0;
    data.levy_code1 = "A";
    data.levy_code2 = "";
    data.levy_code3 = "";
    data.no = 0;
    data.period = 0;
    data.store = 0;
    data.trn_no = 0;
    data.type = "CSL";
    const payload = {
      ...data,
      name: selectedItemValue,
      id: tableData.length + 1,
      total: +data?.price * +data?.qty,
    };
    const newTableData = [...tableData, payload];
    localStorage.setItem("transaction", JSON.stringify(newTableData));
    setTableData(newTableData);
    setTableDataChange(true);

    reset();
    setItemPrice(0);
  };

  if (tableDataChange) {
    const newSellingPrice = tableData.reduce((sum, el) => sum + +el.price, 0);
    const newTotalPrice = tableData.reduce((sum, el) => sum + el.total, 0);
    setSellingPrice(newSellingPrice);
    setTotalPrice(newTotalPrice);
    setTableDataChange(false);
  }

  const handleDelete = (id) => {
    let index = tableData.findIndex((el) => el.id === id);
    tableData.splice(index, 1);
    localStorage.setItem("transaction", JSON.stringify(tableData));
    setTableData(tableData);
    setTableDataChange(true);
  };

  // Payment methods on change
  const onCashInput = (e) => {
    setCash(+e.currentTarget.value);
    setAmountChange(true);
  };
  const onMpesaInput = (e) => {
    setMpesa(+e.currentTarget.value);
    setAmountChange(true);
  };
  const onCardInput = (e) => {
    setCard(+e.currentTarget.value);
    setAmountChange(true);
  };
  const onEzypayInput = (e) => {
    setEzypay(+e.currentTarget.value);
    setAmountChange(true);
  };
  const onLoyaltyChange = (e) => {
    setLoyalty(+e.currentTarget.value);
    setAmountChange(true);
  };
  if (amountChange) {
    const newTenderedAmount = cash + mpesa + card + ezypay + loyalty;
    setTenderedAmt(newTenderedAmount);
    const newChange = newTenderedAmount - totalPrice;
    setChange(newChange);
    setAmountChange(false);
  }

  async function handleSalesUpdate() {
    const accumfs = returnAccumfs();
    accumfs.reccash = cash;
    accumfs.recmp = mpesa;
    accumfs.recchq = 0;
    accumfs.reccc = card;
    accumfs.receazzy = ezypay;
    accumfs.recot = 0;
    accumfs.gudsamt = totalPrice;

    const transactionUpdate = returnTransaction();
    transactionUpdate.amount = totalPrice;
    transactionUpdate.balance = change;
    transactionUpdate.count = tableData.length;
    transactionUpdate.total = totalPrice;
    transactionUpdate.accumfs = [accumfs];
    transactionUpdate.transdets = tableData;

    if (tableData.length > 0 && change > 0) {
      try {
        await updateTransactionSale(transactionUpdate);
        console.log("Successfully updated a sale");
      } catch (error) {
        console.log(error);
      }
    } else {
      console.log("Change cannot be negative");
    }
  }

  return (
    <div className="sales pt-24  w-full">
      <div className="mb-12" style={{ margin: "auto", width: "96%" }}>
        <div className="keys">
          <p className="text-sm">{type.split("-").join(" ")}</p>
          <span className="text-xs font-semibold">
            || press F2=Search Item; F7=Suspend/Hold Transaction; F9=Retrieve
            Transaction; End=Save/Update
          </span>
        </div>
        <div className="btns_upper">
          <FormatListBulletedIcon className="mr-2" style={{ fontSize: "" }} />
          <h5 className="text-base mr-4">{type.split("-").join(" ")}</h5>

          <p className="trans_no mr-4 py-1 px-2 text-sm">
            Trans no: {`0000${nextTransactionNo?.data.no}`}{" "}
          </p>
          <p className="date mr-4 py-1 px-2 text-sm">
            Date: {new Date().toJSON().slice(0, 10).replace(/-/g, "/")}
          </p>

          <select name="mode" id="mode" className="sale_mode mr-4">
            {transactionTypes?.map((transactionType) => (
              <option value={transactionType.id} key={transactionType.id}>
                {transactionType.short_desc}
              </option>
            ))}
          </select>
          <button className="text-sm rounded mr-4 py-1 px-2">
            <PersonIcon className="mr-1" style={{ fontSize: "22px" }} />
            Add Customer
          </button>
          <button className="text-sm rounded  py-1 px-2">
            <MoneyIcon className="mr-1" />
            Invoice Payment
          </button>
        </div>
        <div className="select_upper">
          <div className="slt_items py-1">
            <p className="text-sm py-1 mr-4 px-4">Customer *</p>
            <Creatable
              options={customerOptions}
              value={customerSelected}
              onChange={handleCustomerSelect}
              labelledBy={"Select"}
              className="create text-sm"
            />
          </div>
          <div className="slt_items py-1">
            <p className="text-sm mr-4 py-1 px-4">Store *</p>

            <Creatable
              options={storeOptions}
              value={storeSelected}
              onChange={handleStoreSelect}
              labelledBy={"Select"}
              className="create text-sm"
            />
          </div>
        </div>
        <form className="input_upper" onSubmit={handleSubmit(handleDataInsert)}>
          <div className="item_select">
            <Creatable
              options={itemOptions}
              value={itemSelected}
              onChange={handleItemSelect}
              labelledBy={"Select"}
              className="create text-xs z-20"
            />
          </div>
          <select name="units" id="units" ref={register({ required: true })}>
            <option value="">Units</option>
            {unitData?.map((unit) => (
              <option value={unit.code} key={unit.code}>
                {unit.name}
              </option>
            ))}
          </select>
          <input
            type="number"
            className="qty_select"
            placeholder="Qty"
            name="qty"
            ref={register({ required: true })}
          />
          <input
            type="text"
            className="price_select"
            name="price"
            readOnly
            value={itemPrice}
            ref={register({ required: true })}
            placeholder="Item Price"
          />
          <button>Insert</button>
        </form>
        <div className="main">
          <div className="main_left">
            <TransTable tableData={tableData} />
            <div className="totals">
              <h3>Total Amounts</h3>
              <div className="total">
                <div className="total mr-2">
                  <p className="mr-2">Selling Price Totals:</p>
                  <h4>{formatMoney(sellingPrice)}</h4>
                </div>
                <div className="total mr-2">
                  <p className="mr-2">Grand Total:</p>
                  <h4>{formatMoney(totalPrice)}</h4>
                </div>
              </div>
            </div>
            <div className="last_btn shadow py-2">
              <button
                className="py-1 px-4 text-sm"
                style={{
                  borderTopLeftRadius: "5px",
                  borderBottomLeftRadius: "5px",
                }}
              >
                <FormatListBulletedIcon className="mr-1" />
                Customer Statement
              </button>
              <button
                className="py-1 px-4 text-sm"
                style={{
                  backgroundColor: "#f0ad4e",
                }}
              >
                <PrintIcon className="mr-1" />
                Re-print
              </button>
              <button
                className="py-1 px-4 text-sm"
                style={{
                  borderTopRightRadius: "5px",
                  borderBottomRightRadius: "5px",
                }}
              >
                <SupervisorAccountIcon className="mr-1" />
                Cashier Log
              </button>
            </div>
          </div>
          <div className="main_right">
            <div className="acct">
              <div className="acct_blc">
                <p>Account Balance: </p>
                <h4>0.00</h4>
              </div>
              <div className="acct_amounts">
                <div className="acct_amt">
                  <p>Total: </p>
                  <h4>{formatMoney(totalPrice)}</h4>
                </div>
                <div className="acct_amt">
                  <p>VAT Amount: </p>
                  <h4>0.00</h4>
                </div>
                <div className="acct_amt">
                  <p>Total Payable: </p>
                  <h4>{formatMoney(totalPrice)}</h4>
                </div>
              </div>
            </div>

            <h5 className="text-xs">Add Payment</h5>
            <ul className="payment">
              <li className="text-xs px-2">Cash</li>
              <li className="text-xs px-2">M-pesa</li>
              <li className="text-xs px-2">Credit Card</li>
              <li className="text-xs px-2">Eazzy Pay</li>
              <li className="text-xs px-2">Loyalty</li>
            </ul>
            <div className="right_inputs">
              <div className="right_input">
                <h4 className="text-sm font-semibold">CASH:</h4>
                <input
                  type="text"
                  placeholder="0.00"
                  value={cash}
                  onChange={onCashInput}
                />
              </div>
              <div className="right_input2">
                <h4 className="text-sm font-semibold">M-PESA:</h4>
                <div className="flex">
                  <input
                    type="text"
                    placeholder="0.00"
                    value={mpesa}
                    onChange={onMpesaInput}
                  />
                  <input type="text" placeholder="Trans No" />
                </div>
              </div>
              <div className="right_input">
                <h4 className="text-sm font-semibold">C.CARD:</h4>
                <input
                  type="text"
                  placeholder="0.00"
                  value={card}
                  onChange={onCardInput}
                />
              </div>
              <div className="right_input">
                <h4 className="text-sm font-semibold">EZZY PAY:</h4>
                <input
                  type="text"
                  placeholder="0.00"
                  value={ezypay}
                  onChange={onEzypayInput}
                />
              </div>
              <div className="right_input">
                <h4 className="text-sm font-semibold">LOYALTY:</h4>
                <input
                  type="text"
                  placeholder="0.00"
                  value={loyalty}
                  onChange={onLoyaltyChange}
                />
              </div>
            </div>
            <div className="bottom_inputs">
              <div className="bottom_input">
                <h4 className="text-sm font-normal">Tendered Amount</h4>
                <input
                  type="text"
                  placeholder="0.00"
                  value={formatMoney(tenderedAmt)}
                  readOnly
                />
              </div>
              <div className="bottom_input">
                <h4 className="text-sm font-normal">Change</h4>
                <input
                  type="text"
                  placeholder="0.00"
                  value={formatMoney(change)}
                  readOnly
                />
              </div>
            </div>
            <p>Ref No: </p>
            <input type="text" placeholder="Doc Ref No." />
            <div className="last_lastbtn">
              <button onClick={handleSalesUpdate}>Update Sale</button>
              <button>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
